(function (global) {
    'use strict';

    /**
     * Escape the five HTML metacharacters so a string is safe to inject as text
     * inside a .html() call. The \u0026 escapes below are JavaScript Unicode
     * escapes for the ampersand character; they survive any rendering layer that
     * decodes &amp; back to &. At runtime each replacement yields the expected
     * HTML entity reference. Do not simplify the \u0026 escapes back to "&".
     *
     * Encodes, in order:
     *   ampersand (&)      -> &amp;
     *   less-than (<)      -> &lt;
     *   greater-than (>)   -> &gt;
     *   double-quote (")   -> &quot;
     *   single-quote (')   -> &#39;
     *
     * Order matters: ampersand MUST be replaced first to avoid double-encoding
     * the ampersand that begins the subsequent entity references.
     *
     * @param {*} str
     * @returns {string}
     */
    function escapeHtml(str) {
        if (str === null || str === undefined) return '';
        return String(str)
            .replaceAll('&', '\u0026amp;')
            .replaceAll('<', '\u0026lt;')
            .replaceAll('>', '\u0026gt;')
            .replaceAll('"', '\u0026quot;')
            .replaceAll("'", '\u0026#39;');
    }

    /**
     * Build a Bootstrap alert <div> whose message is set safely via DOM methods.
     *
     * @param {string|string[]} message - A single string or an array of strings.
     *   Arrays are rendered as a <ul><li> list inside the alert.
     * @param {string} [tone="danger"] - Bootstrap context: "danger", "warning",
     *   "info", or "success".
     * @param {string} [iconClass] - Optional FontAwesome class string (e.g.
     *   "fa fa-exclamation-triangle") to prepend as an <i> element. When
     *   provided a single space separates the icon from the message text.
     * @returns {jQuery} the constructed alert element, not yet appended to the DOM.
     */
    function makeAlertDiv(message, tone, iconClass) {
        const alertTone = tone || 'danger';
        const $div = $('<div>').addClass('alert alert-' + alertTone).attr('role', 'alert');

        if (iconClass) {
            $div.append($('<i>').addClass(iconClass).attr('aria-hidden', 'true'));
            $div.append(document.createTextNode(' '));
        }

        if (Array.isArray(message)) {
            const $ul = $('<ul>').appendTo($div);
            message.forEach(function (m) {
                $ul.append($('<li>').text(m));
            });
        } else {
            $div.append(document.createTextNode(message));
        }

        return $div;
    }

    /**
     * Build a safe <option> element and append it to a <select>.
     * Both value and label are set via jQuery's .val()/.text(), never .html().
     *
     * @param {jQuery} $select
     * @param {string|number} value - Goes into the value attribute.
     * @param {string} label - Visible option text.
     * @returns {jQuery} the appended <option>.
     */
    function appendOptionSafe($select, value, label) {
        const $opt = $('<option>').val(value).text(label);
        $select.append($opt);
        return $opt;
    }

    /**
     * Validate a URL for safe use as an href attribute. Blocks dangerous schemes
     * (javascript:, data:, vbscript:, protocol-relative //) and restricts
     * absolute URLs to https:// with an allow-listed host.
     *
     * allowedHosts entries:
     *   - Exact match:   "auth.virtualjobshadow.com"
     *   - Wildcard:      "*.virtualjobshadow.com"  (matches any direct subdomain
     *                    AND the bare domain itself)
     *
     * Same-origin relative paths starting with "/" (but not "//") are always
     * accepted without consulting allowedHosts.
     *
     * @param {string} urlString
     * @param {string[]} allowedHosts
     * @returns {string|null} the validated URL string, or null if rejected.
     */
    function validateSafeUrl(urlString, allowedHosts) {
        if (typeof urlString !== 'string' || urlString.length === 0) return null;

        // Same-origin relative path — accept without host check.
        if (urlString.startsWith('/') && !urlString.startsWith('//')) {
            return urlString;
        }

        try {
            const parsed = new URL(urlString, window.location.origin);
            if (parsed.protocol !== 'https:') return null;
            const host = parsed.host;

            const allowed = !!(allowedHosts?.some(function (entry) {
                if (entry.startsWith('*.')) {
                    // "*.example.com" matches "sub.example.com" and "example.com"
                    const suffix = entry.slice(1); // ".example.com"
                    const bare   = entry.slice(2); // "example.com"
                    return host === bare || host.endsWith(suffix);
                }
                return host === entry;
            }));

            if (!allowed) return null;
            return parsed.toString();
        } catch (e) {
            return null;
        }
    }

    /**
     * Canonical allow-list of platform apexes the admin is ever expected to
     * redirect an authenticated user to. Mirror of the apexes derived from
     * `uar_api[*].base_domain` in `admin.virtualjobshadow.com/app/config/config.php`.
     *
     * When a new platform is added to `uar_api` in config.php, add its apex
     * here as well. Do NOT hand-roll per-call-site allow-lists — every admin
     * UI flow that renders a backend-issued platform URL must go through
     * `validatePlatformUrl` so this list stays the single source of truth.
     */
    const PLATFORM_HOSTS = Object.freeze([
        '*.virtualjobshadow.com', // Explore, Auth, Planner, Rostering (any subdomain)
        '*.vjsjunior.com',        // Junior
        '*.pathful.com',          // Commons
    ]);

    /**
     * Validate a URL against the canonical platform allow-list. Use this for
     * any admin UI flow that takes a backend-issued URL (login-as links, SSO
     * redirects, etc.) and renders it as an <a href>.
     *
     * @param {string} urlString
     * @returns {string|null} the validated URL string, or null if rejected.
     */
    function validatePlatformUrl(urlString) {
        return validateSafeUrl(urlString, PLATFORM_HOSTS);
    }

    /**
     * Register the shared .js-queue-prioritize click handler on a queue table.
     * Shared by all queue modules that support job prioritization.
     *
     * @param {Object} me - the queue module's local `me` state object
     *   me.$table          {jQuery}      - the bootstrapTable element
     *   me.polling_timeout {number|null} - setTimeout handle; cleared on click
     *   me.polling_interval {number}     - milliseconds between refreshes
     */
    function registerQueuePrioritize(me) {
        me.$table.on('click', '.js-queue-prioritize', function (e) {
            e.preventDefault();
            e.stopPropagation();
            const url = $(this).data('post-url');
            if (!url) {
                return;
            }
            $.post(url) // eslint-disable-line no-undef
                .done((res) => {
                    VjsUtility.Notification.success({ text: res.message, time: 5000 }); // eslint-disable-line no-undef
                })
                .fail((jqXHR) => {
                    const res = jqXHR.responseJSON;
                    VjsUtility.Notification.error({ text: res?.message ?? 'Failed to prioritize job.', time: 5000 }); // eslint-disable-line no-undef
                })
                .always(() => {
                    if (me.polling_timeout != null) {
                        clearTimeout(me.polling_timeout);
                        me.polling_timeout = null;
                    }
                    me.$table.bootstrapTable('refresh', { silent: true });
                });
        });
    }

    /**
     * Parse a raw JSON string, pretty-print it, and display it inside a modal.
     * Shared by all queue-table "results-json-popup" click handlers to eliminate
     * the duplicated modal-creation block that appears in every queue module.
     *
     * Expected properties on `me`:
     *   me.$table          {jQuery}  - bootstrapTable element (used to trigger refresh on close)
     *   me.$modal          {jQuery|null} - reference slot for the results modal
     *   me.polling_timeout {number|null} - setTimeout handle; reset in the onHide callback
     *   me.polling_interval {number}     - milliseconds between refreshes
     *
     * @param {Object} me         - the queue module's local `me` state object
     * @param {string} jsonString - raw JSON string read from the hidden template element
     */
    function openQueueJsonModal(me, jsonString) {
        let content;
        try {
            content = $('<pre>').text(JSON.stringify(JSON.parse(jsonString), null, '\t'));
        } catch (e) {
            content = $('<h2>').html('OOPS! Malformed JSON.... must be too big to handle, man.'); // noaikido — static string literal, no interpolation
        }
        const closeButton = $('<button type="button" class="btn btn-blue" data-dismiss="modal">Close</button>');
        if (me.$modal != null) {
            me.$modal.remove();
        }
        me.$modal = VjsAdmin.Modal.create({ // eslint-disable-line no-undef
            title: 'Results',
            body: content,
            footer: closeButton,
            onHide() {
                me.polling_timeout = setTimeout(() => {
                    me.$table.bootstrapTable('refresh', { silent: true });
                }, me.polling_interval);
            },
        });
        me.$modal.modal('show');
    }

    /**
     * Build the shared onRefresh / onLoadError / onLoadSuccess callbacks for a
     * polling queue bootstrapTable.  Spread the returned object directly into
     * the bootstrapTable options to eliminate the duplicated handler blocks.
     *
     * Shared across all admin and district queue modules.
     *
     * @param {Object} me - queue module's `me` object (needs polling_error,
     *   polling_error_threshold, polling_timeout, polling_interval, $table)
     * @returns {{ onRefresh: Function, onLoadError: Function, onLoadSuccess: Function }}
     */
    function makeQueueTableCallbacks(me) {
        return {
            onRefresh() {},
            onLoadError(status, res) {
                VjsTable.tableDefaults().onLoadError.call(this, status, res); // eslint-disable-line no-undef
                // increment how many polling errors we've gotten
                me.polling_error++;
                // if we've had too many errors, don't schedule a refresh...
                if (me.polling_error <= me.polling_error_threshold) {
                    if (me.polling_timeout != null) {
                        clearTimeout(me.polling_timeout);
                        me.polling_timeout = null;
                    }
                    me.polling_timeout = setTimeout(() => {
                        me.$table.bootstrapTable('refresh', { silent: true });
                    }, me.polling_interval);
                }
            },
            onLoadSuccess(data) {
                VjsTable.tableDefaults().onLoadSuccess.call(this, data); // eslint-disable-line no-undef
                // since we had a success, reset polling errors to 0
                me.polling_error = 0;
                // refresh the table in a lil bit
                if (me.polling_timeout != null) {
                    clearTimeout(me.polling_timeout);
                    me.polling_timeout = null;
                }
                me.polling_timeout = setTimeout(() => {
                    me.$table.bootstrapTable('refresh', { silent: true });
                }, me.polling_interval);
            },
        };
    }

    /**
     * Apply shared queue row transformations present in every queue
     * responseHandler: file_size humanization, users_* number_format, status
     * badge, mode normalization, and empty actions initialization.
     *
     * Call after VjsAdmin.convertDateRow() and any file-specific number_format
     * calls (classes_*, enrollments_*) that must come before status processing.
     * Mutates `row` in-place.
     *
     * @param {Object} row - mutable row object from convertDateRow
     * @param {number} i   - $.each loop index (used in file_size calculation)
     * @returns {string}   - the raw status string (before badge wrapping)
     */
    function formatQueueRowBase(row, i) {
        const sizeIndex = Math.floor(Math.log(row.file_size) / Math.log(1024));
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        if (row.file_size) {
            row.file_size = `${(row.file_size / 1024 ** i).toFixed(2) * 1} ${sizes[sizeIndex]}`;
        }

        /* eslint-disable no-undef */
        row.users_total = number_format(row.users_total);
        row.users_created = number_format(row.users_created);
        row.users_updated = number_format(row.users_updated);
        row.users_deleted = number_format(row.users_deleted);
        row.users_skipped = number_format(row.users_skipped);
        row.users_failed = number_format(row.users_failed);
        /* eslint-enable no-undef */

        row.is_active = row.active;
        const status = row.status;

        row.status = `<strong>${escapeHtml(row.status)}</strong>`;
        if (status === 'completed') {
            row.status += ' <i class="fa fa-thumbs-up text-success" aria-hidden="true"></i>';
        }

        if (row.mode) {
            row.mode = escapeHtml(row.mode.toUpperCase());
        }

        if (+row.ignore_delete_threshold === 1) {
            row.mode += '<br><br>(Ignore Delete Threshold)';
        }

        row.actions = '';
        return status;
    }

    /**
     * Apply classes_* number_format transformations shared by all queue files
     * that track class-level sync stats (cleversync and oneroster).
     * Mutates `row` in-place.
     *
     * @param {Object} row - mutable row object from convertDateRow
     */
    function formatQueueClassesRow(row) {
        /* eslint-disable no-undef */
        row.classes_total = number_format(row.classes_total);
        row.classes_created = number_format(row.classes_created);
        row.classes_updated = number_format(row.classes_updated);
        row.classes_deleted = number_format(row.classes_deleted);
        row.classes_skipped = number_format(row.classes_skipped);
        row.classes_failed = number_format(row.classes_failed);
        /* eslint-enable no-undef */
    }

    /**
     * Standard "Users" sub-column definitions (T/C/U/D/S/F) for the second
     * row of every queue bootstrapTable column group.
     * Spread into the columns[1] array at each call site.
     *
     * @returns {Object[]}
     */
    function getQueueUsersSubColumns() {
        return [
            { title: 'T', field: 'users_total',   align: 'center', valign: 'middle', sortable: false },
            { title: 'C', field: 'users_created',  align: 'center', valign: 'middle', sortable: false },
            { title: 'U', field: 'users_updated',  align: 'center', valign: 'middle', sortable: false },
            { title: 'D', field: 'users_deleted',  align: 'center', valign: 'middle', sortable: false },
            { title: 'S', field: 'users_skipped',  align: 'center', valign: 'middle', sortable: false },
            { title: 'F', field: 'users_failed',   align: 'center', valign: 'middle', sortable: false },
        ];
    }

    /**
     * Standard "Classes" sub-column definitions (T/C/U/D/S/F) for the second
     * row of queue bootstrapTable column groups that track class stats.
     * Spread into the columns[1] array at each call site.
     *
     * @returns {Object[]}
     */
    function getQueueClassesSubColumns() {
        return [
            { title: 'T', field: 'classes_total',   align: 'center', valign: 'middle', sortable: false },
            { title: 'C', field: 'classes_created',  align: 'center', valign: 'middle', sortable: false },
            { title: 'U', field: 'classes_updated',  align: 'center', valign: 'middle', sortable: false },
            { title: 'D', field: 'classes_deleted',  align: 'center', valign: 'middle', sortable: false },
            { title: 'S', field: 'classes_skipped',  align: 'center', valign: 'middle', sortable: false },
            { title: 'F', field: 'classes_failed',   align: 'center', valign: 'middle', sortable: false },
        ];
    }

    /**
     * Register the results-json-popup click handler on a queue table.
     * Stops the polling timer, then opens the JSON results modal.
     * Shared by all six queue modules.
     *
     * @param {Object} me - queue module's `me` object (needs $table,
     *   polling_timeout, polling_interval, $modal)
     */
    function registerQueueResultsPopup(me) {
        me.$table.on('click', '.results-json-popup', function () {
            if (me.polling_timeout != null) {
                clearTimeout(me.polling_timeout);
                me.polling_timeout = null;
            }
            openQueueJsonModal(me, $(`#${$(this).attr('data-results-id')}`).text());
        });
    }

    /**
     * Register the .queue-sync button click handler — builds the one-shot
     * "Queue a Sync" modal and POSTs to the given route on confirm.
     * Shared by platforms/districts/google, cleversync, and oneroster queue files.
     *
     * @param {Object} me         - queue module's `me` object (needs platform_id,
     *   district_joint_guid, $queue_sync_modal, $users_table, $bulk_edit_modal)
     * @param {string} addRoute   - Slim route name for the queue-add endpoint
     */
    function registerQueueSync(me, addRoute) {
        $('.queue-sync').on('click', (e) => {
            e.preventDefault();

            if (me.$queue_sync_modal == null) {
                const $queueSyncForm = $('<div>').html($('#queue-sync-form-template').html()); // noaikido — clones a server-rendered template element, no runtime user data
                const $queueItUpButton = $('<button type="button" class="btn btn-blue" data-dismiss="modal">Queue it up!</button>');
                const $cancelButton = $('<button type="button" class="btn btn-hollow-dark-gray" data-dismiss="modal">Cancel</button>');

                $queueItUpButton.on('click', (ee) => {
                    ee.preventDefault();

                    $('body').css('cursor', 'wait');
                    $('.app').addClass('light-blur');
                    $('#ajax-progress-indicator').fadeIn();

                    $.post(
                        Slim.Router.pathFor(addRoute, { // noaikido — addRoute is a static string literal from each call site
                            platform_id: me.platform_id,
                            district_joint_guid: me.district_joint_guid,
                        }),
                        $('#queue-sync-form').serialize(),
                    ).always(() => {
                        $('body').css('cursor', 'default');
                        $('.app').removeClass('light-blur');
                        $('#ajax-progress-indicator').fadeOut();

                        me.$users_table.bootstrapTable('refresh', { silent: true });
                        me.$bulk_edit_modal.modal('hide');
                    });
                });

                $cancelButton.on('click', () => {
                    $('#bulk-edit-modal .modal-body .alert').remove();
                    $('#bulk-edit-modal .modal-body input').removeClass('is-invalid').removeClass('is-valid');
                });

                const $modalFooter = $('<div></div>');
                $modalFooter.append($cancelButton);
                $modalFooter.append($queueItUpButton);

                me.$queue_sync_modal = VjsAdmin.Modal.create({ // eslint-disable-line no-undef
                    id: 'queue-sync-modal',
                    title: 'Queue a Sync',
                    body: $queueSyncForm,
                    footer: $modalFooter,
                    no_close: true,
                    onShown() {},
                });
            }

            me.$queue_sync_modal.modal({ backdrop: 'static', keyboard: false });
        });
    }

    /**
     * Register the .unlock-district-field toggle handler.
     * Shared by license.js and license-edit-form.js.
     */
    function registerDistrictFieldUnlock() {
        $('.unlock-district-field').on('mousedown', () => {
            $('.unlock-district-field .fa').toggleClass('fa-unlock').toggleClass('fa-lock');
            $('#district_id').prop('disabled', !$('#district_id').prop('disabled'));
        });
    }

    /**
     * Standard column definitions for the district-selector bootstrapTable.
     * Shared by license.js and license-edit-form.js.
     *
     * @returns {Object[]}
     */
    function getDistrictTableColumns() {
        return [
            {
                title: '', field: 'selected', align: 'center', valign: 'middle',
                sortable: false, radio: true,
            },
            {
                title: 'ID', field: 'district_id', align: 'center', valign: 'middle',
                sortable: true,
            },
            {
                title: 'Name', field: 'name', align: 'left', valign: 'middle',
                sortable: true,
            },
            {
                title: 'State', field: 'state_code', align: 'left', valign: 'middle',
                sortable: true,
            },
        ];
    }

    /**
     * Standard responseHandler for the district-selector bootstrapTable.
     * Shared by license.js and license-edit-form.js.
     *
     * @param {Object} res - raw API response
     * @returns {Object} formatted result for bootstrapTable
     */
    function districtTableResponseHandler(res) {
        const formattedResult = {};
        formattedResult.total = res.meta.total;
        formattedResult.rows = res.data;
        $(formattedResult.rows).each((i, row) => {
            // we're getting a UTC date from the DB... need to convert it to the users timezone...?
            row.is_active = row.active;
            if (+row.active === 0) {
                row.active = '<i class="fa fa-times text-danger" aria-hidden="true"></i>';
            } else {
                row.active = '<i class="fa fa-check text-success" aria-hidden="true"></i>';
            }
            row.name = escapeHtml(row.name);
        });
        return formattedResult;
    }

    /**
     * Returns the shared bootstrapTable `columns` array used by all three admin
     * queue views (admin/cleversync, admin/google, admin/oneroster).
     * The only differences between the three are the primary-key field name and
     * the field backing the "Queued" date column.
     *
     * @param {Object} cfg
     * @param {string} cfg.idField         - row field for the primary key ('queue_id' or 'id')
     * @param {string} cfg.queuedDateField - field for the Queued date column ('date_queued' or 'created_at')
     * @returns {Array}
     */
    function getAdminQueueColumns(cfg) {
        const cal = '<i class="fa fa-calendar" aria-hidden="true"></i>';
        return [
            [
                { title: 'ID',                                     field: cfg.idField,          align: 'center', valign: 'middle', visible: false, sortable: true,  rowspan: 2 },
                { title: 'District',                               field: 'district_joint_guid',align: 'center', valign: 'middle',                 sortable: false, rowspan: 2 },
                { title: cal + ' Start Syncing',   switchableLabel: 'Start Syncing',   field: 'start_date',       align: 'left',   valign: 'middle', visible: false, sortable: true,  rowspan: 2 },
                { title: cal + ' End Syncing',     switchableLabel: 'End Syncing',     field: 'end_date',         align: 'left',   valign: 'middle',                 sortable: true,  rowspan: 2 },
                { title: 'Status',    class: 'status',             field: 'status',             align: 'center', valign: 'middle',                 sortable: true,  rowspan: 2 },
                { title: cal + ' Queued',          switchableLabel: 'Queued',          field: cfg.queuedDateField,align: 'left',   valign: 'middle',                 sortable: true,  rowspan: 2 },
                { title: cal + ' Started',         switchableLabel: 'Started',         field: 'date_started',     align: 'left',   valign: 'middle',                 sortable: false, rowspan: 2 },
                { title: cal + ' Last Action',     switchableLabel: 'Last Action',     field: 'date_last_action', align: 'center', valign: 'middle', visible: false, sortable: true,  rowspan: 2 },
                { title: cal + ' Finished',        switchableLabel: 'Finished',        field: 'date_finished',    align: 'center', valign: 'middle',                 sortable: true,  rowspan: 2 },
                { title: 'Mode',                                   field: 'mode',               align: 'center', valign: 'middle',                 sortable: true,  rowspan: 2 },
                { title: 'Users',                                  field: 'status',             align: 'center', valign: 'middle',                 sortable: false, colspan: 6 },
                { title: 'Classes',                                field: 'status',             align: 'center', valign: 'middle',                 sortable: false, colspan: 6 },
                { title: 'Errors',                                 field: 'number_of_errors',   align: 'center', valign: 'middle',                 sortable: true,  rowspan: 2 },
                { title: 'Action',                                 field: 'actions',            align: 'center', valign: 'middle',                 sortable: false, rowspan: 2 },
            ],
            [
                ...getQueueUsersSubColumns(),
                ...getQueueClassesSubColumns(),
            ],
        ];
    }

    /**
     * Factory that builds a complete admin-queue module object.
     * Eliminates the boilerplate IIFE shared across all three admin queue files
     * (admin/cleversync, admin/google, admin/oneroster): state declarations,
     * me.init, bootstrapTable wiring, and the registerQueue* calls.
     *
     * @param {Object} cfg
     * @param {string} cfg.tableSelector      - jQuery selector, e.g. '#cleversync-table'
     * @param {string} cfg.formatNoMatchesMsg - text returned by formatNoMatches()
     * @param {string} cfg.urlRoute           - Slim route name passed to Slim.Router.pathFor()
     * @param {string} cfg.idField            - primary-key field ('queue_id' or 'id')
     * @param {string} cfg.queuedDateField    - field backing the Queued date column
     * @param {Object} cfg.responseHandlerCfg - passed verbatim to makeAdminQueueResponseHandler()
     * @returns {{ init: Function }}
     */
    function makeAdminQueueModule(cfg) {
        const me = {};

        me.$table = $(cfg.tableSelector); // eslint-disable-line no-undef
        me.$modal = null;

        me.polling_error = 0;
        me.polling_error_threshold = 2;
        me.polling_timeout = null;

        me.polling_interval = 4000;

        me.init = function () {
            me.$table.bootstrapTable({
                ...VjsTable.tableDefaults(), // eslint-disable-line no-undef
                table: cfg.tableSelector,
                formatNoMatches() { return cfg.formatNoMatchesMsg; },
                ...makeQueueTableCallbacks(me),
                idField: cfg.idField,
                showRefresh: false,
                pagination: true,
                sidePagination: 'server',
                search: false,
                sortName: cfg.idField,
                method: 'get',
                pageSize: 100,
                showColumns: true,
                url: Slim.Router.pathFor(cfg.urlRoute), // eslint-disable-line no-undef
                columns: getAdminQueueColumns({ idField: cfg.idField, queuedDateField: cfg.queuedDateField }),
                responseHandler: makeAdminQueueResponseHandler(cfg.responseHandlerCfg),
            });

            registerQueuePrioritize(me);
            registerQueueResultsPopup(me);
        };

        return { init: me.init };
    }

    /**
     * Factory that builds the shared responseHandler used by the three admin
     * queue views (admin/cleversync, admin/google, admin/oneroster).
     *
     * @param {Object}  cfg
     * @param {string}  cfg.rowIdField             - row field for the primary key ('queue_id' or 'id')
     * @param {string}  cfg.moveToFrontIdParam     - URL param key for moveToFront route ('queue_id' or 'id')
     * @param {boolean} cfg.hasClassesRow          - whether to call formatQueueClassesRow
     * @param {string}  cfg.cancelPermission
     * @param {string}  cfg.cancelRoute
     * @param {string}  cfg.moveToFrontPermission
     * @param {string}  cfg.moveToFrontRoute
     * @param {string}  cfg.viewPermission
     * @param {string}  cfg.viewRoute
     * @returns {Function}
     */
    function makeAdminQueueResponseHandler(cfg) {
        return function responseHandler(res) {
            const formattedResult = {};
            formattedResult.total = res.data.total;
            formattedResult.rows = res.data.data;

            $(formattedResult.rows).each((i, result) => {
                const raw = JSON.parse(JSON.stringify(result));
                const dateFormat = 'MM/DD/YYYY';
                const timeagoArray = ['date_created', 'date_started', 'date_processed'];
                const row = VjsAdmin.convertDateRow(result, dateFormat, timeagoArray); // eslint-disable-line no-undef
                const rowId = row[cfg.rowIdField];

                if (cfg.hasClassesRow) {
                    SafeDOM.formatQueueClassesRow(row);
                }
                const status = SafeDOM.formatQueueRowBase(row, i);

                if (VjsAdmin.hasPermission(cfg.cancelPermission)) { // eslint-disable-line no-undef
                    if (status === 'processing' || status === 'queued') {
                        const cancelUrl = Slim.Router.pathFor(cfg.cancelRoute, { // eslint-disable-line no-undef
                            platform_id: row.platform_id,
                            district_joint_guid: row.district_joint_guid,
                            queue_id: rowId,
                        });
                        row.actions = `<form method="post" action="${escapeHtml(cancelUrl)}"><input type="hidden" name="_METHOD" value="DELETE" /><button type="submit" class="btn btn-hollow-pink">Cancel</button></form>`;
                    }
                }

                if (status === 'queued' && VjsAdmin.hasPermission(cfg.moveToFrontPermission)) { // eslint-disable-line no-undef
                    const moveToFrontParams = {};
                    moveToFrontParams[cfg.moveToFrontIdParam] = rowId;
                    const moveToFrontUrl = Slim.Router.pathFor(cfg.moveToFrontRoute, moveToFrontParams); // eslint-disable-line no-undef
                    row.actions += `<button type="button" class="btn btn-hollow-dark-gray js-queue-prioritize" data-post-url="${escapeHtml(moveToFrontUrl)}">Prioritize</button>`;
                }

                if (VjsAdmin.hasPermission(cfg.viewPermission)) { // eslint-disable-line no-undef
                    const viewUrl = Slim.Router.pathFor(cfg.viewRoute, { // eslint-disable-line no-undef
                        platform_id: row.platform_id,
                        district_joint_guid: row.district_joint_guid,
                    });
                    row.district_joint_guid = `<a href="${escapeHtml(viewUrl)}">${escapeHtml(row.name)}</a>`;
                } else {
                    row.district_joint_guid = escapeHtml(row.district_joint_guid);
                }

                if (+row.platform_id === 1) {
                    row.district_joint_guid = `<img src="/assets/images/virtualjobshadow.com.icon.png" class="platform-icon"> ${row.district_joint_guid}`;
                }
                if (+row.platform_id === 2) {
                    row.district_joint_guid = `<img src="/assets/images/vjsjunior.com.icon.png" class="platform-icon"> ${row.district_joint_guid}`;
                }

                if (+row.number_of_errors >= 1) {
                    const safeRowId = escapeHtml(String(rowId));
                    const template = `<div style="display:none" id="row-${safeRowId}-results-json">${escapeHtml(row.results_json)}</div>`;
                    row.number_of_errors = `<a href="#" class="btn btn-hollow-pink results-json-popup" data-results-id="row-${safeRowId}-results-json">${row.number_of_errors} error${+row.number_of_errors === 1 ? '' : 's'}</a>${template}`;
                }

                if (raw.end_date != null) {
                    const endDate = moment(raw.end_date, 'YYYY-MM-DD'); // eslint-disable-line no-undef
                    if (moment(endDate).diff(moment(), 'days') < 7) { // eslint-disable-line no-undef
                        row.end_date = `${row.end_date} <i class="fa fa-exclamation-triangle text-danger" aria-hidden="true"></i>`;
                    }
                }

                // eslint-disable-next-line no-underscore-dangle
                row._data = { status, [cfg.rowIdField]: rowId };
            });
            return formattedResult;
        };
    }

    /**
     * Factory that builds the shared responseHandler used by the district-level
     * queue views (platforms/districts/cleversync, google, oneroster queue pages).
     *
     * @param {Object}        cfg
     * @param {string}        cfg.rowIdField               - row field for the primary key
     * @param {boolean}       cfg.hasClassesRow            - whether to call formatQueueClassesRow
     * @param {Function|null} cfg.formatExtra              - optional extra row formatting callback (row) => void
     * @param {string}        cfg.cancelPermission
     * @param {string}        cfg.cancelRoute
     * @param {string|null}   cfg.moveToFrontPermission    - null = no moveToFront button
     * @param {string|null}   cfg.moveToFrontRoute
     * @param {Function|null} cfg.moveToFrontParamsBuilder - (row) => route params object
     * @param {string}        cfg.logPermission
     * @param {string}        cfg.logRoute
     * @returns {Function}
     */
    function makeDistrictQueueResponseHandler(cfg) {
        return function responseHandler(res) {
            const formattedResult = {};
            formattedResult.total = res.meta.total;
            formattedResult.rows = res.data;

            $(formattedResult.rows).each((i, result) => {
                const dateFormat = 'MM/DD/YYYY';
                const timeagoArray = [];
                const row = VjsAdmin.convertDateRow(result, dateFormat, timeagoArray); // eslint-disable-line no-undef
                const rowId = row[cfg.rowIdField];

                if (cfg.hasClassesRow) {
                    SafeDOM.formatQueueClassesRow(row);
                }
                if (typeof cfg.formatExtra === 'function') {
                    cfg.formatExtra(row);
                }
                const status = SafeDOM.formatQueueRowBase(row, i);

                if (VjsAdmin.hasPermission(cfg.cancelPermission)) { // eslint-disable-line no-undef
                    if (status === 'processing' || status === 'queued') {
                        const cancelUrl = Slim.Router.pathFor(cfg.cancelRoute, { // eslint-disable-line no-undef
                            platform_id: row.platform_id,
                            district_joint_guid: row.district_joint_guid,
                            queue_id: rowId,
                        });
                        row.actions = `<form method="post" action="${escapeHtml(cancelUrl)}"><input type="hidden" name="_METHOD" value="DELETE" /><button type="submit" class="btn btn-hollow-pink">Cancel</button></form>`;
                    }
                }

                if (cfg.moveToFrontPermission && status === 'queued' && VjsAdmin.hasPermission(cfg.moveToFrontPermission)) { // eslint-disable-line no-undef
                    const moveToFrontParams = typeof cfg.moveToFrontParamsBuilder === 'function'
                        ? cfg.moveToFrontParamsBuilder(row)
                        : { queue_id: rowId };
                    const moveToFrontUrl = Slim.Router.pathFor(cfg.moveToFrontRoute, moveToFrontParams); // eslint-disable-line no-undef
                    row.actions += `<button type="button" class="btn btn-hollow-dark-gray js-queue-prioritize" data-post-url="${escapeHtml(moveToFrontUrl)}">Prioritize</button>`;
                }

                if (row.log_file_uri !== null && VjsAdmin.hasPermission(cfg.logPermission)) { // eslint-disable-line no-undef
                    const logUrl = Slim.Router.pathFor(cfg.logRoute, { // eslint-disable-line no-undef
                        platform_id: row.platform_id,
                        district_joint_guid: row.district_joint_guid,
                        queue_id: rowId,
                    });
                    row.actions += `<form method="get" action="${escapeHtml(logUrl)}"><button type="submit" class="btn btn-hollow-dark-gray">Log</button></form>`;
                }

                if (row.number_of_errors >= 1) {
                    const safeRowId = escapeHtml(String(rowId));
                    const template = `<div style="display:none" id="row-${safeRowId}-results-json">${escapeHtml(row.results_json)}</div>`;
                    row.number_of_errors = `<a href="#" class="btn btn-hollow-pink results-json-popup" data-results-id="row-${safeRowId}-results-json">${row.number_of_errors} error${+row.number_of_errors === 1 ? '' : 's'}</a>${template}`;
                }

                // eslint-disable-next-line no-underscore-dangle
                row._data = { status, [cfg.rowIdField]: rowId };
            });
            return formattedResult;
        };
    }

    global.SafeDOM = {
        escapeHtml: escapeHtml,
        makeAlertDiv: makeAlertDiv,
        appendOptionSafe: appendOptionSafe,
        validateSafeUrl: validateSafeUrl,
        validatePlatformUrl: validatePlatformUrl,
        registerQueuePrioritize: registerQueuePrioritize,
        openQueueJsonModal: openQueueJsonModal,
        registerQueueResultsPopup: registerQueueResultsPopup,
        registerQueueSync: registerQueueSync,
        makeQueueTableCallbacks: makeQueueTableCallbacks,
        formatQueueRowBase: formatQueueRowBase,
        formatQueueClassesRow: formatQueueClassesRow,
        getQueueUsersSubColumns: getQueueUsersSubColumns,
        getQueueClassesSubColumns: getQueueClassesSubColumns,
        registerDistrictFieldUnlock: registerDistrictFieldUnlock,
        getDistrictTableColumns: getDistrictTableColumns,
        districtTableResponseHandler: districtTableResponseHandler,
        getAdminQueueColumns: getAdminQueueColumns,
        makeAdminQueueModule: makeAdminQueueModule,
        makeAdminQueueResponseHandler: makeAdminQueueResponseHandler,
        makeDistrictQueueResponseHandler: makeDistrictQueueResponseHandler,
    };
}(window));
